import OrderArticle from "../models/order/OrderArticle";
import store, { useAppDispatch } from "../redux/store";
import { orderArticlesPushedByUser } from "../redux/shoppingCartSlice";
import { useCallback, useEffect, useRef } from "react";
import Rule from "../automation/Rule";
import useUpsellMenuTrigger from "../../../kiosk/utils/useUpsellMenuTrigger";
import { toast } from "react-toastify";
import FormattedMessageJamezz from "../../components/FormattedMessageJamezz";
import useOpenUpsellPage, { retrieveValidArticles } from "../../domains/upsells/hooks/useOpenUpsellPage.tsx";
import ShowArticleDialogAction from "../automation/actions/ShowArticleDialogAction";
import {
  ArrangementCheckResult,
  useArrangementChecks,
} from "../../components/ArrangementLimitations/ArrangementLimitations.tsx";
import { AgeCheckResult } from "../ageCheck/ageCheckUtils.ts";
import { useAgeCheck } from "../../components/AgeCheckDialog.tsx";
import { getArticleById } from "../models/menu/Menu.ts";
import { selectArticlesMap } from "../redux/selectors/selectArticlesMap.ts";
import _ from "lodash";
import useRulesForArticle from "../../domains/upsells/hooks/useRulesForArticle.ts";
import { useLazyGetUpsellSuggestionsQuery } from "../upsells/api/upsellsApi.tsx";
import isKiosk from "../helpers/isKiosk.tsx";
import KioskArticleSuggestionPage from "../../../kiosk/pages/ArticleSuggestionPage/ArticleSuggestionPage.tsx";
import QrArticleSuggestionPage from "../../../qr/pages/ArticleSuggestionPage/ArticleSuggestionPage.tsx";
import SlideUpTransition from "../../components/SlideUpTransition.tsx";
import useMenuFilters from "../menu/filters/useMenuFilters.ts";
import { useDialog } from "../dialog/DialogProvider.tsx";
import useIsWidescreen from "../useIsWidescreen.ts";
import { differenceInMilliseconds } from "date-fns";
import { BlockUserInput } from "@jamezz/react-components";
import { v7 } from "uuid";

export function checkStockAndMaxCount(orderArticle: OrderArticle, orderArticles: OrderArticle[]) {
  if (
    (orderArticle.article.maxCount && orderArticle.article.maxCount > 0) ||
    (orderArticle.article.stock > 0 && store.getState().global.salesarea.useProductStock)
  ) {
    const articleFromStore =
      getArticleById(selectArticlesMap(store.getState()), orderArticle.article.id) ?? orderArticle.article;
    const maxCount = articleFromStore.maxCount ?? articleFromStore.stock;
    let currentCount = 0;
    orderArticles.forEach((item) => {
      if (item.article.id === orderArticle.article.id) {
        currentCount += item.count;
      }
    });

    if (currentCount + orderArticle.count > maxCount) {
      if (articleFromStore.maxCount) {
        toast.error(
          <FormattedMessageJamezz
            id={"You can order maximal {maxCount} of this item."}
            values={{ maxCount: maxCount }}
          />
        );
      } else {
        toast.error(<FormattedMessageJamezz id={"Only {x} left in stock"} values={{ x: articleFromStore.stock }} />, {
          toastId: "x_left_in_stock_" + articleFromStore.id,
        });
      }
      return false;
    }
  }
  return true;
}

export default function useAddOrderArticleToShoppingCart(
  { allowUpsell }: { allowUpsell: boolean } = { allowUpsell: true }
) {
  const { openDialog } = useDialog();
  const dispatch = useAppDispatch();
  const checkArrangements = useArrangementChecks();
  const checkAge = useAgeCheck();

  const { triggerUpsellMenu } = useUpsellMenuTrigger();
  const getRulesForOrderArticle = useRulesForArticle();
  const openUpsellPage = useOpenUpsellPage();
  const [fetch] = useLazyGetUpsellSuggestionsQuery();
  const isWidescreen = useIsWidescreen();
  const articleFilter = useMenuFilters();
  // const { blockingStarted, blockingStopped } = BlockUserInput.useBlockUserInput();
  const loadingUuid = useRef("");

  useEffect(() => {
    loadingUuid.current = v7().toString();
    return () => {
      console.log("STOPPED 4");
    };
  }, []);

  return useCallback(
    (orderArticle: OrderArticle) => {
      return new Promise<void>((resolve, reject) => {
        (async () => {
          const orderArticles = store.getState().shoppingCart.items;
          if (!checkStockAndMaxCount(orderArticle, orderArticles)) {
            reject("stock_denied");
            return;
          }

          const arrangementRulesCheck = await checkArrangements(orderArticle);
          if (arrangementRulesCheck === ArrangementCheckResult.Accepted) {
            const ageCheckResult = await checkAge(orderArticle);
            if (ageCheckResult === AgeCheckResult.Denied) {
              reject("age_denied");
              return;
            }
          } else {
            reject("arrangement_check_denied");
            return;
          }

          dispatch(orderArticlesPushedByUser([orderArticle]));
          resolve();

          _.defer(() => {
            if (allowUpsell) {
              if (store.getState().global.salesarea.custom_data?.ai?.use_upsell_suggestion_api ?? false) {
                if (
                  !orderArticle.isUpselled &&
                  !(store.getState().global.salesarea.custom_data.upsell_enabled === false)
                ) {
                  const time = new Date();
                  const uuid = loadingUuid.current;
                  BlockUserInput.blockingStarted(uuid);

                  setTimeout(() => {
                    BlockUserInput.blockingStopped(uuid);
                    // console.log("STOPPED 1");
                  }, 5000);

                  fetch(
                    store
                      .getState()
                      .shoppingCart.items.map((item) => ({ name: item.article.name, quantity: item.count }))
                  )
                    .then((response) => {
                      BlockUserInput.blockingStopped(uuid);
                      // console.log("STOPPED 2");
                      const newTime = new Date();

                      if (differenceInMilliseconds(newTime, time) <= 5000 && response.data?.data) {
                        const ArticleSuggestionPage = isKiosk() ? KioskArticleSuggestionPage : QrArticleSuggestionPage;
                        const articles = retrieveValidArticles(
                          articleFilter,
                          response.data.data.map((item) => String(item.id))
                        );

                        if (articles.length > 0) {
                          openDialog({
                            children: (
                              <ArticleSuggestionPage
                                articles={articles}
                                upsellType={"AiUpsell"}
                                upsellId={0}
                                upsellSource={undefined}
                                // title={action.titleTranslations?.[lang] ?? action.title}
                                // text={action.descriptionTranslations?.[lang] ?? action.description}
                                useAsDialog={true}
                                showBottomHideButton={true}
                              />
                            ),
                            fullScreen: isKiosk() ? "almostFullScreen" : !isWidescreen,
                            TransitionComponent: SlideUpTransition,
                          });
                        }
                      }
                    })
                    .finally(() => {
                      BlockUserInput.blockingStopped(uuid);
                      // console.log("STOPPED 3");
                    });
                }
              } else {
                if (orderArticle.article.upsellMenu) {
                  triggerUpsellMenu(orderArticle);
                }
                if (!orderArticle.isUpselled) {
                  getRulesForOrderArticle(orderArticle.article, orderArticle.articlegroup)
                    ?.filter((rule: Rule) => rule.conditions.every((condition) => condition.check()))
                    .forEach((rule: Rule) => {
                      if (
                        rule.action?.discriminator === "ShowArticleDialogAction" &&
                        rule.action?.upsellVariant == "upsell"
                      ) {
                        const action = rule.action as ShowArticleDialogAction;
                        openUpsellPage(action, orderArticle.article.id);
                      }
                    });
                }
              }
            }
          });
        })();
      });
    },
    [
      checkArrangements,
      dispatch,
      checkAge,
      allowUpsell,
      fetch,
      articleFilter,
      openDialog,
      isWidescreen,
      triggerUpsellMenu,
      getRulesForOrderArticle,
      openUpsellPage,
    ]
  );
}
